/**
 * AdminSidebar — left nav with logical clusters.
 *
 *   NOW       — at-a-glance dashboard
 *   IDENTITY  — Users & orgs
 *   DATA      — Sites, Site contexts, Ghost docs
 *   MODELS    — DAM, Report types
 *   LIBRARY   — Curator queue, Coverage snapshot, Coverage trends, …
 *   TELEMETRY — Provider Posture, Health
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Users, BarChart3, FileText,
  Layers, BookMarked, TrendingUp, Activity, Server, ShieldCheck,
  Ghost, Truck,
  Bug,
  ClipboardList,
  DollarSign,
  Cog,
} from 'lucide-react';

/** Stable union of every navigable surface. Used by AdminPanel.tsx to
 *  switch which tab renders. Adding a new admin page = add a key here and
 *  the corresponding ITEMS entry. */
export type AdminSidebarKey =
  | 'now'
  | 'identity'
  | 'users' | 'companies' | 'members'
  /** R-131-SUP-A — admin impersonation (restored with the f7bb7a69 recovery). */
  | 'impersonate'
  | 'sites' | 'site-contexts'
  /** DLB-4 — Ghost Docs (tombstone inspection + purge). */
  | 'ghost-docs'
  | 'dam'   | 'report-types'
  | 'band-config'
  | 'cluster-config'
  | 'ops'
  | 'library-curator' | 'library-coverage' | 'library-coverage-trends'
  | 'supplier-directory'
  | 'reactor-vendors'
  /** Document Matching Quality DM7 — diagnostic + tenant override panel. */
  | 'doc-matching-quality'
  /** DMS-FOLLOWUP-ADMIN-MISCLASSIFIED-PAGE (DA-1) — dedicated triage
   *  page for mis-classified docs, fed by the DA-2 persistent metrics. */
  | 'dms-misclassified'
  /** Citation_Traceability CT9 — admin Citation Quality dashboard. */
  | 'citation-quality'
  /** PP pack — composes PRE-4 / LLW-4 / SCE-5 audit-log telemetry into a
   *  single Provider Posture page so admins don't have to grep raw logs. */
  | 'provider-posture'
  | 'health'
  | 'bugs'
  | 'audit-queue'
  /** R-131-EVAL-F — Bradley-Terry human eval pairwise voting. */
  | 'eval'
  | 'sed-rebuild-log'
  | 'job-executions'
  | 'billing'
  /** Cost & Spend — per-org/per-user platform spend across all costs. */
  | 'cost-spend'
  | 'sentiment-sources';

/** All routable admin keys — used for URL validation / legacy-route fallback. */
export const ADMIN_SIDEBAR_KEYS: readonly AdminSidebarKey[] = [
  'now', 'identity', 'users', 'companies', 'members', 'impersonate',
  'sites', 'site-contexts', 'ghost-docs',
  'dam', 'report-types', 'band-config', 'cluster-config', 'ops',
  'library-curator', 'library-coverage', 'library-coverage-trends',
  'supplier-directory', 'reactor-vendors', 'citation-quality',
  'doc-matching-quality', 'dms-misclassified',
  'provider-posture', 'health', 'bugs', 'audit-queue', 'eval', 'sed-rebuild-log', 'job-executions', 'billing', 'cost-spend', 'sentiment-sources',
] as const;

export function isAdminSidebarKey(value: string): value is AdminSidebarKey {
  return (ADMIN_SIDEBAR_KEYS as readonly string[]).includes(value);
}

/** Counts that drive the badge column. All optional — 0 / undefined renders
 *  no badge. */
export interface AdminSidebarCounts {
  curatorCandidates?: number;
  pendingKeyRequests?: number;
  /** Site_Context SC9 — count of sites with at least one unacknowledged
   *  HIGH-severity site-context conflict. Drives the badge on the
   *  "Site contexts" sidebar entry and the KPI on the Now dashboard. */
  outstandingSiteContextConflicts?: number;
  /** Citation_Traceability CT9 — count of reports currently flagged as
   *  below the citation-density floor. Drives the warning badge on the
   *  "Citation quality" sidebar entry. */
  citationQualityFlags?: number;
}

interface AdminSidebarProps {
  activeKey: AdminSidebarKey;
  /** Invoked AFTER the `<Link>` navigation. Used by the mobile-sheet wrapper
   *  in AdminLayout to close the drawer on item click, and by AdminPanel as
   *  a back-compat hook (the URL is now the source of truth, but the panel
   *  also mirrors the active tab into local state for the content switch).
   */
  onSelect: (key: AdminSidebarKey) => void;
  counts: AdminSidebarCounts;
  hasSelectedCompany: boolean;
  /** Collapse to icon-only between md and lg breakpoints. Only honoured on
   *  the desktop sidebar; the mobile sheet always renders full-width. */
  iconOnlyBelowLg?: boolean;
}

/**
 * Map a sidebar key to its URL path. The "now" landing lives at the bare
 * `/admin` so the dropdown entry "Admin Panel" can navigate to a single
 * canonical URL without picking an arbitrary first tab. Exported so the
 * AdminSidebar tests can assert the URL contract without mocking router.
 */
export function keyToHref(key: AdminSidebarKey): string {
  if (key === 'now') return '/admin';
  if (key === 'band-config') return '/admin/band-config';
  if (key === 'cluster-config') return '/admin/cluster-config';
  if (key === 'ops') return '/admin/ops';
  return `/admin/${key}`;
}

interface Item {
  key: AdminSidebarKey;
  label: string;
  icon: React.ReactNode;
  /** Optional accessor → count. Returning 0 or undefined hides the badge. */
  countFrom?: (c: AdminSidebarCounts) => number | undefined;
  /** Tone of the count badge — drives colour. */
  countTone?: 'info' | 'warning';
  /** Conditional gate, e.g. members-only-when-company-selected. */
  visibleWhen?: (ctx: { hasSelectedCompany: boolean }) => boolean;
}

interface Cluster {
  label: string;
  items: Item[];
}

const CLUSTERS: Cluster[] = [
  {
    label: '',
    items: [
      { key: 'now', label: 'Now', icon: <Home className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: 'Identity',
    items: [
      { key: 'identity', label: 'Users & orgs', icon: <Users className="w-3.5 h-3.5" /> },
      { key: 'impersonate', label: 'Impersonate', icon: <Users className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: 'Data',
    items: [
      { key: 'sites', label: 'Sites', icon: <BarChart3 className="w-3.5 h-3.5" /> },
      {
        key: 'site-contexts', label: 'Site contexts',
        icon: <FileText className="w-3.5 h-3.5" />,
        countFrom: c => c.outstandingSiteContextConflicts, countTone: 'warning',
      },
      {
        key: 'ghost-docs', label: 'Ghost docs',
        icon: <Ghost className="w-3.5 h-3.5" />,
      },
    ],
  },
  {
    label: 'Models',
    items: [
      { key: 'dam',          label: 'DAM',          icon: <FileText className="w-3.5 h-3.5" /> },
      { key: 'report-types', label: 'Report types', icon: <Layers className="w-3.5 h-3.5" /> },
      { key: 'band-config',  label: 'Decision bands', icon: <BarChart3 className="w-3.5 h-3.5" /> },
      { key: 'cluster-config', label: 'Geo clusters', icon: <BarChart3 className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: 'Library',
    items: [
      {
        key: 'library-curator', label: 'Curator queue',
        icon: <BookMarked className="w-3.5 h-3.5" />,
        countFrom: c => c.curatorCandidates, countTone: 'info',
      },
      { key: 'library-coverage',        label: 'Coverage snapshot', icon: <Layers className="w-3.5 h-3.5" /> },
      { key: 'library-coverage-trends', label: 'Coverage trends',   icon: <TrendingUp className="w-3.5 h-3.5" /> },
      { key: 'supplier-directory', label: 'Supplier directory', icon: <Truck className="w-3.5 h-3.5" /> },
      { key: 'reactor-vendors', label: 'Reactor vendors', icon: <Layers className="w-3.5 h-3.5" /> },
      {
        key: 'citation-quality', label: 'Citation quality',
        icon: <ShieldCheck className="w-3.5 h-3.5" />,
        countFrom: c => c.citationQualityFlags, countTone: 'warning',
      },
    ],
  },
  {
    label: 'Quality',
    items: [
      { key: 'doc-matching-quality', label: 'Doc matching', icon: <Layers className="w-3.5 h-3.5" /> },
      { key: 'dms-misclassified', label: 'DMS · Misclassified', icon: <ShieldCheck className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: 'Telemetry',
    items: [
      {
        key: 'provider-posture', label: 'Provider Posture',
        icon: <Activity className="w-3.5 h-3.5" />,
      },
      {
        key: 'health', label: 'Health',
        icon: <Server className="w-3.5 h-3.5" />,
        countFrom: c => c.pendingKeyRequests, countTone: 'warning',
      },
      {
        key: 'bugs', label: 'Bug Queue',
        icon: <Bug className="w-3.5 h-3.5" />,
      },
      {
        key: 'audit-queue', label: 'Audit Queue',
        icon: <ClipboardList className="w-3.5 h-3.5" />,
      },
      {
        key: 'eval', label: 'Human eval',
        icon: <ClipboardList className="w-3.5 h-3.5" />,
      },
      {
        key: 'sed-rebuild-log', label: 'SED rebuild log',
        icon: <ShieldCheck className="w-3.5 h-3.5" />,
      },
      {
        key: 'job-executions', label: 'Job executions',
        icon: <Cog className="w-3.5 h-3.5" />,
      },
      {
        key: 'ops', label: 'Ops console',
        icon: <Activity className="w-3.5 h-3.5" />,
      },
      {
        key: 'billing', label: 'Billing',
        icon: <DollarSign className="w-3.5 h-3.5" />,
      },
      {
        key: 'cost-spend', label: 'Cost & Spend',
        icon: <DollarSign className="w-3.5 h-3.5" />,
      },
      {
        key: 'sentiment-sources', label: 'Sentiment sources',
        icon: <Activity className="w-3.5 h-3.5" />,
      },
    ],
  },
];

export function AdminSidebar({
  activeKey,
  onSelect,
  counts,
  hasSelectedCompany,
  iconOnlyBelowLg = false,
}: AdminSidebarProps) {
  return (
    <nav className="py-3 w-full" data-testid="admin-sidebar">
      {CLUSTERS.map((cluster, ci) => {
        const visibleItems = cluster.items.filter(i =>
          !i.visibleWhen || i.visibleWhen({ hasSelectedCompany }),
        );
        if (visibleItems.length === 0) return null;
        return (
          <div key={`cluster-${ci}`} className="mb-2">
            {cluster.label && (
              <div
                className={`px-3 py-1.5 type-body font-semibold uppercase tracking-widest text-text-muted ${
                  iconOnlyBelowLg ? 'lg:block hidden' : ''
                }`}
              >
                {cluster.label}
              </div>
            )}
            {visibleItems.map(item => {
              const count = item.countFrom?.(counts);
              const showBadge = typeof count === 'number' && count > 0;
              const isActive = activeKey === item.key;
              return (
                <Link
                  key={item.key}
                  to={keyToHref(item.key)}
                  onClick={() => onSelect(item.key)}
                  data-testid={`admin-sidebar-item-${item.key}`}
                  data-active={isActive ? 'true' : 'false'}
                  className={`w-full flex items-center gap-2 h-9 px-3 border-l-2 transition-colors text-left no-underline ${
                    isActive
                      ? 'border-primary bg-state-active text-text-primary'
                      : 'border-transparent text-text-secondary hover:bg-state-hover hover:text-text-primary'
                  }`}
                  title={item.label}
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span
                    className={`flex-1 text-[12px] truncate ${
                      iconOnlyBelowLg ? 'lg:inline hidden' : ''
                    }`}
                  >
                    {item.label}
                  </span>
                  {showBadge && (
                    <span
                      data-testid={`admin-sidebar-badge-${item.key}`}
                      className={`shrink-0 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 type-body font-bold leading-none ${
                        item.countTone === 'warning'
                          ? 'bg-warning text-white'
                          : 'bg-primary/15 text-primary'
                      } ${iconOnlyBelowLg ? 'lg:inline-flex hidden' : ''}`}
                    >
                      {count}
                    </span>
                  )}
                  {showBadge && iconOnlyBelowLg && (
                    <span
                      aria-hidden="true"
                      className={`lg:hidden inline-block w-1.5 h-1.5 rounded-full ${
                        item.countTone === 'warning' ? 'bg-warning' : 'bg-primary'
                      }`}
                    />
                  )}
                </Link>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
